import { FC, useEffect, useRef } from 'react'
import BookmarkIconFilled from 'assets/img/bookmark-icon-filled.svg'

import styled from 'styled-components'
import {
  ModalCard as SharedModalCard,
  ModalOverlay as SharedModalOverlay,
  ModalTitle
} from 'components/modals/styles'
import { QuestionBookmarkIcon, QuestionButton } from 'common/common-styles'
import ChevronIcon from 'components/ChevronIcon/ChevronIcon'

const Overlay = styled(SharedModalOverlay)`
  z-index: 200;
`

const ModalCard = styled(SharedModalCard)`
  width: 641px;
  min-width: 641px;
  min-height: 312px;
  padding: 15px 25px 25px;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    min-width: 300px;
  }
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CloseButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const Title = styled(ModalTitle)``

const LegendRow = styled.div`
  margin-top: 25px;
  margin-left: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
`

const LegendText = styled.span`
  color: ${(props) => props.theme.colors.text.legend};
  font-family: ${(props) => props.theme.typography.fontInter};
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
`

const AnsweredDot = styled.span<{ color?: string }>`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: ${(props) => props.color || props.theme.colors.surface.error};
  display: inline-block;
`

const AnsweredIndicator = styled(AnsweredDot)`
  background: ${(props) => props.theme.colors.surface.success};
`

const CurrentDash = styled.span`
  width: 14px;
  height: 2px;
  background: ${(props) => props.theme.colors.border.primary};
  display: inline-block;
`

const QuestionsPanel = styled.div`
  margin-top: 10px;
  border-radius: 8px;
  background: ${(props) => props.theme.colors.surface.hover};
  padding: 14px;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
  }
`

const QuestionsInnerPanel = styled.div`
  border-radius: 8px;
  background: ${(props) => props.theme.colors.card.background};
  width: 100%;
  height: 100%;
  height: 164px;
  padding: 8px;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
  }
`

const BookmarkLegendIcon = styled.img`
  width: 11px;
  height: 11px;
`

const QuestionsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  justify-content: flex-start;
  gap: 7px;
`

const QuestionSelectorModal: FC<any> = ({
  showQuestionSelectorModal,
  setShowQuestionSelectorModal,
  assessmentName,
  questions,
  currentQuestionIndex,
  onSelectQuestion,
  isReadOnlyViewMode = false
}) => {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!showQuestionSelectorModal) return

    /**
     * Start keyboard navigation inside the open question selector and return
     * focus to its opener when the dialog closes.
     */
    const previouslyFocusedElement = document.activeElement as HTMLElement
    closeButtonRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowQuestionSelectorModal(false)
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      previouslyFocusedElement?.focus()
    }
  }, [setShowQuestionSelectorModal, showQuestionSelectorModal])

  if (!showQuestionSelectorModal) {
    return null
  }

  return (
    <Overlay onClick={() => setShowQuestionSelectorModal(false)}>
      <ModalCard
        role='dialog'
        aria-modal='true'
        aria-label='Question selector'
        onClick={(event) => event.stopPropagation()}
      >
        <ModalHeader>
          <CloseButton
            ref={closeButtonRef}
            type='button'
            aria-label='Close question selector'
            onClick={() => setShowQuestionSelectorModal(false)}
          >
            <ChevronIcon
              direction='left'
              onClick={() => setShowQuestionSelectorModal(false)}
            />
          </CloseButton>
          <Title>{assessmentName} Questions</Title>
        </ModalHeader>

        <LegendRow>
          <LegendItem>
            <BookmarkLegendIcon src={BookmarkIconFilled} alt='bookmark' />
            <LegendText>Bookmarked</LegendText>
          </LegendItem>

          <LegendItem>
            <AnsweredIndicator />
            <LegendText>Answered</LegendText>
          </LegendItem>

          <LegendItem style={{ alignItems: 'flex-end' }}>
            <CurrentDash />
            <LegendText>Current</LegendText>
          </LegendItem>
        </LegendRow>

        <QuestionsPanel>
          <QuestionsInnerPanel>
            <QuestionsGrid>
              {questions.map((question: any, index: any) => {
                /**
                 * Submitted Benchmark A2 review keeps the correct answer in
                 * `answer` and the student's response in `student_answer`.
                 * The selector must therefore use the submitted response to
                 * show whether that question was answered.
                 */
                const answered = isReadOnlyViewMode
                  ? question.student_answer !== null
                  : question.answer !== null
                const isCurrentQuestion = currentQuestionIndex === index
                const isBookmarked = Boolean(question?.bookmark)
                return (
                  <QuestionButton
                    key={question.id || index}
                    type='button'
                    $answered={answered}
                    $current={isCurrentQuestion}
                    onClick={() => {
                      onSelectQuestion(index)
                      setShowQuestionSelectorModal(false)
                    }}
                    aria-label={`Question ${index + 1}`}
                  >
                    {isBookmarked && <QuestionBookmarkIcon />}
                    {index + 1}
                  </QuestionButton>
                )
              })}
            </QuestionsGrid>
          </QuestionsInnerPanel>
        </QuestionsPanel>
      </ModalCard>
    </Overlay>
  )
}

export default QuestionSelectorModal
